import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Button } from '@/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/ui/dialog';

interface PrivacySettingsProps {
  trigger?: React.ReactNode;
  onClose?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const PrivacySettings: React.FC<PrivacySettingsProps> = ({
  trigger,
  onClose,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { grantConsent, revokeConsent, hasConsent } = useAnalytics();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const [currentConsent, setCurrentConsent] = useState(hasConsent());

  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    else setInternalOpen(v);
  };

  const handleConsentChange = (granted: boolean) => {
    if (granted) {
      grantConsent();
    } else {
      revokeConsent();
    }
    setCurrentConsent(granted);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      onClose?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        className="max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('privacy.settings.title', 'Privacy Settings')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {t('privacy.settings.analytics.title', 'Analytics & Cookies')}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {t(
                  'privacy.settings.analytics.description',
                  'Help us improve our service by allowing analytics data collection.',
                )}
              </p>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('privacy.settings.analytics.label', 'Allow Analytics')}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {currentConsent
                    ? t('privacy.settings.analytics.enabled', 'Currently enabled')
                    : t('privacy.settings.analytics.disabled', 'Currently disabled')}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={currentConsent ? 'default' : 'outline'}
                  onClick={() => handleConsentChange(true)}
                  className="text-xs px-3 py-1"
                >
                  {t('privacy.settings.enable', 'Enable')}
                </Button>
                <Button
                  size="sm"
                  variant={!currentConsent ? 'default' : 'outline'}
                  onClick={() => handleConsentChange(false)}
                  className="text-xs px-3 py-1"
                >
                  {t('privacy.settings.disable', 'Disable')}
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {t('privacy.settings.dataCollection.title', 'What We Collect')}
            </h4>
            <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <li>
                • {t('privacy.settings.dataCollection.pageViews', 'Page views and navigation')}
              </li>
              <li>
                •{' '}
                {t(
                  'privacy.settings.dataCollection.interactions',
                  'Button clicks and interactions',
                )}
              </li>
              <li>• {t('privacy.settings.dataCollection.performance', 'Performance metrics')}</li>
              <li>• {t('privacy.settings.dataCollection.errors', 'Error reports (anonymous)')}</li>
            </ul>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                'privacy.settings.note',
                'Your privacy settings are stored locally and can be changed at any time.',
              )}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacySettings;
